// BuscadorCursos.js — Buscador del header (barra «Buscar un curso» o tecla «/»).
// Busca en los cursos de la sección `catalogo_streaming` del proyecto por
// título, categoría, especialidad, docente y sinopsis.
//
// En pantalla ancha es búsqueda con vista previa: a la izquierda los resultados,
// a la derecha el curso señalado (con su avance en video, descripción y
// botones). Se señala con el cursor o con las flechas del teclado. En el
// celular queda solo la lista. «Contenido» abre la ficha del catálogo (evento
// `catalogo:abrir`, o /?curso=<key> desde otra página).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { BACKEND_BASE } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useMiPlan from '../../hooks/useMiPlan';

const aparecer = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const bajar = keyframes`from { opacity: 0; transform: translateY(-14px); } to { opacity: 1; transform: none; }`;
const fundido = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
const normal = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const sinHtml = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const menosMovimiento = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ---------- Estilos ----------
const Fondo = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  overflow-y: auto;
  padding: clamp(16px, 6vh, 64px) 16px 40px;
  background: rgba(3, 4, 7, 0.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  animation: ${aparecer} 0.18s ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Caja = styled.div`
  --acento: ${p => p.$acento};
  --sobre-acento: ${p => p.$sobreAcento};
  --panel: color-mix(in srgb, ${p => p.$fondo} 82%, #fff 4%);
  width: min(1180px, 100%);
  margin: 0 auto;
  color: #eef2f7;
  font-family: ${p => p.$fuente || 'inherit'};
  animation: ${bajar} 0.26s cubic-bezier(0.2, 0.8, 0.2, 1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

// Campo: grande, con el borde del acento mientras se escribe.
const Campo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 72px;
  padding: 0 14px 0 22px;
  border-radius: 14px;
  background: var(--panel);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1), 0 24px 60px -30px rgba(0, 0, 0, 0.9);
  transition: box-shadow 0.2s;
  &:focus-within { box-shadow: inset 0 0 0 2px var(--acento), 0 24px 60px -30px rgba(0, 0, 0, 0.9); }
  > svg { flex: none; width: 26px; height: 26px; color: var(--acento); }
  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #fff;
    font: inherit;
    font-size: clamp(1.2rem, 3.2vw, 1.75rem);
    font-weight: 600;
    font-stretch: 88%;
    letter-spacing: -0.01em;
    &::placeholder { color: #6c7686; font-weight: 500; }
    &::-webkit-search-cancel-button { display: none; }
  }
  @media (max-width: 600px) { height: 60px; padding: 0 10px 0 16px; gap: 12px; > svg { width: 22px; height: 22px; } }
`;

const Mini = styled.button`
  flex: none;
  height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
  color: #aeb7c4;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.13); color: #fff; }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  .tacto { display: none; }
  @media (hover: none) { .tecla { display: none; } .tacto { display: inline; } }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 18px 0 0;
  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    color: #c9d0da;
    font: inherit;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    small { font-size: 0.74rem; font-weight: 700; color: #7f8a9a; font-variant-numeric: tabular-nums; }
    &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
    &[aria-pressed='true'] { background: #fff; color: #07090d; small { color: #5b6573; } }
    &[data-vacio='1'] { opacity: 0.42; }
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  }
`;

const Cuerpo = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.08fr);
  gap: 28px;
  margin-top: 22px;
  align-items: start;
  @media (max-width: 960px) { grid-template-columns: minmax(0, 1fr); }
`;

const Resumen = styled.p`
  margin: 0 0 8px 4px;
  font-size: 0.84rem;
  color: #7f8a9a;
`;

const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Fila = styled.li`
  button {
    width: 100%;
    display: grid;
    grid-template-columns: 124px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    padding: 8px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
    ${p => p.$activa && css`
      background: rgba(255, 255, 255, 0.08);
      .foto { box-shadow: 0 0 0 2px var(--acento); }
    `}
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
    @media (max-width: 520px) { grid-template-columns: 96px minmax(0, 1fr); gap: 12px; }
  }
  .foto {
    aspect-ratio: 16 / 9;
    border-radius: 7px;
    overflow: hidden;
    background: #1c212a;
    display: grid;
    place-items: center;
    color: #fff;
    font-weight: 800;
    font-stretch: 75%;
    font-size: 1.4rem;
    transition: box-shadow 0.2s;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
  strong { display: block; font-size: 1rem; font-weight: 650; color: #fff; line-height: 1.25; }
  .meta { display: block; margin-top: 4px; font-size: 0.82rem; color: #8b96a6; }
  mark { background: none; color: var(--acento); }
`;

// Vista previa: el curso señalado, grande. Queda fija mientras se recorre la lista.
const Vista = styled.aside`
  position: sticky;
  top: 0;
  border-radius: 16px;
  overflow: hidden;
  background: var(--panel);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08), 0 30px 80px -40px rgba(0, 0, 0, 0.95);
  @media (max-width: 960px) { display: none; }
  .pantalla {
    position: relative;
    aspect-ratio: 16 / 9;
    background: #0b0e13;
    img, video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; animation: ${fundido} 0.35s ease-out; }
    &::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to top, var(--panel) 0%, transparent 42%); pointer-events: none; }
  }
  .texto { padding: 0 26px 26px; margin-top: -40px; position: relative; }
  .cat { display: inline-block; margin-bottom: 8px; font-size: 0.8rem; font-weight: 600; color: #c9d0da; }
  h3 { margin: 0; font-size: clamp(1.5rem, 2.3vw, 2rem); font-weight: 800; font-stretch: 80%; line-height: 1.02; letter-spacing: -0.01em; color: #fff; text-wrap: balance; }
  .datos { display: flex; gap: 14px; margin-top: 10px; font-size: 0.86rem; color: #aab3c0; }
  p { margin: 14px 0 0; font-size: 0.95rem; line-height: 1.55; color: #d3d9e2; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
  .botones { display: flex; gap: 10px; margin-top: 20px; }
`;

const Accion = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px 0 16px;
  border: 0;
  border-radius: 6px;
  font: inherit;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, box-shadow 0.18s;
  svg { width: 18px; height: 18px; flex: none; }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  ${p => p.$lleno ? css`
    flex: 1.3 1 0; background: #fff; color: #06080c;
    &:hover { background: var(--acento); color: var(--sobre-acento); }
  ` : css`
    flex: 1 1 0; background: rgba(255, 255, 255, 0.1); color: #fff; box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.24);
    &:hover { background: rgba(255, 255, 255, 0.18); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5); }
  `}
`;

const Vacio = styled.div`
  grid-column: 1 / -1;
  padding: 56px 16px;
  text-align: center;
  color: #8b96a6;
  strong { display: block; margin-bottom: 8px; color: #fff; font-size: 1.15rem; font-weight: 650; }
  button { margin-top: 18px; }
`;

const IcoLupa = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" />
  </svg>
);
const IcoPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15a1 1 0 0 0 1.52.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 7 4.5Z" /></svg>
);
const IcoAbajo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
);

// Resalta en el título lo que se escribió (sin distinguir tildes).
const Resaltado = ({ texto, q }) => {
  const nq = normal(q).trim();
  if (!nq) return texto;
  const i = normal(texto).indexOf(nq);
  if (i < 0) return texto;
  return <>{texto.slice(0, i)}<mark>{texto.slice(i, i + nq.length)}</mark>{texto.slice(i + nq.length)}</>;
};

const BuscadorCursos = ({ abierto, onCerrar, proyecto, acento = '#5ee0d2', textoAcento = '#04201d', fondo = '#0d1624', fuente }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const inputRef = useRef(null);
  const listaRef = useRef(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [activa, setActiva] = useState(0);

  // Cursos del catálogo, con los datos del curso real cuando está enlazado.
  const cfg = useMemo(() => (proyecto?.config_json?.landing_secciones || [])
    .find(s => s.tipo === 'catalogo_streaming' && s.visible !== false)?.config || {}, [proyecto]);
  // Plan de formación: con subrol especial solo sus cursos; Inactivo, ninguno.
  const mp = useMiPlan();
  const cursos = useMemo(() => {
    const docs = proyecto?.documentos || [];
    const permitidos = mp.modo === 'plan' ? new Set((mp.cursos || []).map(String)) : null;
    if (mp.modo === 'inactivo') return [];
    return (Array.isArray(cfg.items) ? cfg.items : [])
      .filter(it => !permitidos || (it.documento_id && permitidos.has(String(it.documento_id))))
      .map((it, i) => {
      const doc = it.documento_id ? docs.find(d => String(d.id) === String(it.documento_id)) : null;
      const modulos = doc?.modulos || [];
      const c = {
        key: it.key || `item${i}`,
        documento_id: it.documento_id,
        titulo: it.titulo || doc?.titulo || 'Curso',
        categoria: (it.categoria || '').trim(),
        especialidad: (it.especialidad || '').trim(),
        docente: it.docente || doc?.docente || '',
        imagen: it.imagen_cartelera || it.imagen || doc?.imagen || '',
        miniatura: it.imagen || doc?.imagen || '',
        trailer: it.trailer || '',
        sinopsis: sinHtml(it.sinopsis || doc?.descripcion),
        n_modulos: modulos.length,
        n_lecciones: modulos.reduce((n, m) => n + (m.lecciones?.length || 0), 0),
      };
      c.texto = normal([c.titulo, c.categoria, c.especialidad, c.docente, c.sinopsis].join(' '));
      return c;
    });
  }, [cfg, proyecto, mp.modo, mp.cursos]);

  // Filtro por categoría (la clasificación de la portada); si no hay, por especialidad.
  const campoFiltro = cursos.some(c => c.categoria) ? 'categoria' : 'especialidad';
  // Lo que calza con lo escrito; los números de los filtros cuentan sobre eso.
  const porTexto = useMemo(() => {
    const palabras = normal(q).split(/\s+/).filter(Boolean);
    return cursos.filter(c => palabras.every(p => c.texto.includes(p)));
  }, [cursos, q]);
  const grupos = useMemo(() => {
    const n = {};
    cursos.forEach(c => { const g = c[campoFiltro]; if (g) n[g] = 0; });
    porTexto.forEach(c => { const g = c[campoFiltro]; if (g) n[g] += 1; });
    return Object.entries(n);
  }, [cursos, porTexto, campoFiltro]);
  const resultados = useMemo(() => porTexto.filter(c => !cat || c[campoFiltro] === cat), [porTexto, cat, campoFiltro]);

  useEffect(() => { setActiva(0); }, [q, cat]);

  useEffect(() => {
    if (!abierto) return undefined;
    setQ(''); setCat('');
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputRef.current?.focus(), 20);
    return () => { document.body.style.overflow = overflow; };
  }, [abierto]);

  // Con las flechas, la fila señalada se mantiene a la vista.
  useEffect(() => {
    listaRef.current?.querySelector(`[data-i="${activa}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activa]);

  if (!abierto) return null;

  const verFicha = (c) => {
    onCerrar();
    if (location.pathname === '/') window.dispatchEvent(new CustomEvent('catalogo:abrir', { detail: { key: c.key } }));
    else navigate(`/?curso=${encodeURIComponent(c.key)}`);
  };
  // «Ver curso»: con sesión entra al curso; sin sesión, la ficha (desde ahí se inscribe).
  const verCurso = (c) => {
    if (user && c.documento_id) { onCerrar(); navigate(`/mentor/${c.documento_id}`); } else verFicha(c);
  };

  const onTecla = (e) => {
    if (e.key === 'Escape') onCerrar();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiva(i => Math.min(i + 1, resultados.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiva(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && e.target === inputRef.current && resultados[activa]) { e.preventDefault(); verFicha(resultados[activa]); }
  };

  const filtrando = q.trim() || cat;
  const sel = resultados[activa] || resultados[0];
  const meta = (c) => [c.categoria || c.especialidad, c.n_lecciones ? `${c.n_lecciones} ${c.n_lecciones === 1 ? 'lección' : 'lecciones'}` : '']
    .filter(Boolean).join('  ·  ');

  return createPortal(
    <Fondo onMouseDown={e => { if (e.target === e.currentTarget) onCerrar(); }} onKeyDown={onTecla}>
      <Caja $acento={acento} $sobreAcento={textoAcento} $fondo={fondo} $fuente={fuente} role="dialog" aria-modal="true" aria-label="Buscar cursos">
        <Campo>
          <IcoLupa />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={typeof window !== 'undefined' && window.innerWidth < 600 ? 'Busca un curso o tema' : 'Busca un curso, un tema o un docente'}
            aria-label="Buscar cursos"
            aria-controls="buscador-resultados"
            aria-activedescendant={sel ? `curso-${sel.key}` : undefined}
            autoComplete="off"
          />
          {q && <Mini type="button" onClick={() => { setQ(''); inputRef.current?.focus(); }}>Borrar</Mini>}
          <Mini type="button" onClick={onCerrar} aria-label="Cerrar el buscador"><span className="tecla">Esc</span><span className="tacto">Cerrar</span></Mini>
        </Campo>

        {grupos.length > 1 && (
          <Chips role="group" aria-label="Filtrar por categoría">
            <button type="button" aria-pressed={!cat} onClick={() => setCat('')}>Todos <small>{porTexto.length}</small></button>
            {grupos.map(([g, n]) => (
              <button key={g} type="button" aria-pressed={cat === g} data-vacio={n === 0 && cat !== g ? 1 : 0} onClick={() => setCat(cat === g ? '' : g)}>{g} <small>{n}</small></button>
            ))}
          </Chips>
        )}

        <Cuerpo>
          {resultados.length > 0 ? (
            <>
              <div>
                <Resumen aria-live="polite">
                  {filtrando
                    ? `${resultados.length} ${resultados.length === 1 ? 'curso' : 'cursos'}`
                    : 'Todos los cursos'}
                </Resumen>
                <Lista id="buscador-resultados" role="listbox" ref={listaRef}>
                  {resultados.map((c, i) => (
                    <Fila key={c.key} $activa={c === sel}>
                      <button
                        id={`curso-${c.key}`}
                        data-i={i}
                        type="button"
                        role="option"
                        aria-selected={c === sel}
                        onMouseEnter={() => setActiva(i)}
                        onFocus={() => setActiva(i)}
                        onClick={() => verFicha(c)}
                      >
                        <span className="foto">{c.miniatura ? <img src={urlImg(c.miniatura)} alt="" loading="lazy" /> : c.titulo.charAt(0)}</span>
                        <span>
                          <strong><Resaltado texto={c.titulo} q={q} /></strong>
                          {meta(c) && <span className="meta">{meta(c)}</span>}
                        </span>
                      </button>
                    </Fila>
                  ))}
                </Lista>
              </div>

              {sel && (
                <Vista aria-label={`Vista previa: ${sel.titulo}`}>
                  <div className="pantalla">
                    {sel.trailer && !menosMovimiento()
                      ? <video key={sel.key} src={urlImg(sel.trailer)} poster={urlImg(sel.imagen)} muted autoPlay loop playsInline />
                      : sel.imagen && <img key={sel.key} src={urlImg(sel.imagen)} alt="" />}
                  </div>
                  <div className="texto">
                    {(sel.categoria || sel.especialidad) && <span className="cat">{sel.categoria || sel.especialidad}</span>}
                    <h3>{sel.titulo}</h3>
                    {(sel.n_modulos > 0 || sel.n_lecciones > 0) && (
                      <div className="datos">
                        {sel.n_modulos > 0 && <span>{sel.n_modulos} {sel.n_modulos === 1 ? 'módulo' : 'módulos'}</span>}
                        {sel.n_lecciones > 0 && <span>{sel.n_lecciones} {sel.n_lecciones === 1 ? 'lección' : 'lecciones'}</span>}
                      </div>
                    )}
                    {sel.sinopsis && <p>{sel.sinopsis}</p>}
                    <div className="botones">
                      <Accion $lleno type="button" onClick={() => verCurso(sel)}><IcoPlay />{cfg.texto_ver || 'Ver curso'}</Accion>
                      <Accion type="button" onClick={() => verFicha(sel)}><IcoAbajo />{cfg.texto_info || 'Contenido'}</Accion>
                    </div>
                  </div>
                </Vista>
              )}
            </>
          ) : (
            <Vacio aria-live="polite">
              <strong>Ningún curso coincide con «{q.trim() || cat}»{q.trim() && cat ? ` en ${cat}` : ''}</strong>
              Prueba con otra palabra: el nombre del curso, un tema o el docente.
              <div>
                <Accion type="button" onClick={() => { setQ(''); setCat(''); inputRef.current?.focus(); }}>Ver todos los cursos</Accion>
              </div>
            </Vacio>
          )}
        </Cuerpo>
      </Caja>
    </Fondo>,
    document.body
  );
};

export default BuscadorCursos;
